import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Activity, Webhook, AlertTriangle, Crown, RotateCcw, Trash2, XCircle } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState("overview");
  const [busyUser, setBusyUser] = useState("");

  const load = useCallback(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/users").then((r) => setUsers(r.data)),
      api.get("/admin/webhook-logs").then((r) => setLogs(r.data)),
    ]).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const runUserAction = async (user, action) => {
    const isDeleted = user.status === "deleted";
    let request;
    let success;

    if (action === "delete") {
      if (!window.confirm(`Delete ${user.email}? This can be recovered later.`)) return;
      request = api.post(`/admin/users/${user.id}/delete`, { reason: "Admin deleted account" });
      success = "Account deleted. Recovery is available.";
    } else if (action === "recover") {
      request = api.post(`/admin/users/${user.id}/recover`);
      success = "Account recovered.";
    } else if (action === "permanent") {
      if (!isDeleted) {
        toast.error("Soft-delete the account before permanent deletion.");
        return;
      }
      const typed = window.prompt(`Permanent delete cannot be recovered. Type DELETE to remove ${user.email}.`);
      if (typed !== "DELETE") return;
      request = api.delete(`/admin/users/${user.id}/permanent`);
      success = "Account permanently deleted.";
    }

    setBusyUser(`${user.id}:${action}`);
    try {
      await request;
      toast.success(success);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyUser("");
    }
  };

  if (!stats) return <div className="brutal p-6 inline-block">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <header>
        <span className="chip bg-accent">Admin</span>
        <h1 className="text-4xl md:text-5xl font-extrabold mt-3">Operations Console</h1>
        <p className="text-muted-foreground font-medium mt-1">Users, usage, webhook logs and abuse detection.</p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="brutal p-5 bg-white" data-testid="admin-users-count">
          <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"><Users className="w-3.5 h-3.5" strokeWidth={2.5} /> Users</div>
          <div className="font-heading font-black text-4xl mt-2">{stats.users_total}</div>
          <div className="text-xs text-muted-foreground font-semibold mt-1">{stats.users_active} active · {stats.users_deleted} deleted · {stats.users_paid} Pro</div>
        </div>
        <div className="brutal p-5 bg-mint">
          <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" strokeWidth={2.5} /> DMs sent</div>
          <div className="font-heading font-black text-4xl mt-2">{stats.dms_sent}</div>
        </div>
        <div className="brutal p-5 bg-peach">
          <div className="text-xs font-bold uppercase tracking-wider">DMs failed</div>
          <div className="font-heading font-black text-4xl mt-2">{stats.dms_failed}</div>
        </div>
        <div className="brutal p-5 bg-butter">
          <div className="text-xs font-bold uppercase tracking-wider">Rules</div>
          <div className="font-heading font-black text-4xl mt-2">{stats.rules_total}</div>
        </div>
      </div>

      {stats.high_volume_users.length > 0 && (
        <div className="brutal p-5 bg-destructive text-white">
          <div className="flex items-center gap-2 font-bold mb-2"><AlertTriangle className="w-4 h-4" strokeWidth={2.5} /> Abuse alert: high-volume users</div>
          <ul className="text-sm font-semibold space-y-1">
            {stats.high_volume_users.map((u) => (
              <li key={u.id}>{u.email} — {u.count} triggers this month</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 border-b-2 border-foreground">
        {[
          { v: "overview", label: "Users" },
          { v: "logs", label: "Webhook Logs" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            data-testid={`admin-tab-${t.v}`}
            className={`px-4 py-2 font-bold text-sm border-2 border-b-0 border-foreground rounded-t-md ${tab === t.v ? "bg-primary" : "bg-white"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="brutal bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted border-b-2 border-foreground">
                <tr>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Name</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Email</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">User ID</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Status</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Plan</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Rules</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Triggers</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">This Month</th>
                  <th className="text-left p-3 font-bold uppercase text-xs tracking-wider">Joined</th>
                  <th className="text-right p-3 font-bold uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const deleted = u.status === "deleted";
                  const admin = u.role === "admin";
                  return (
                  <tr key={u.id} className={`border-b border-foreground/20 ${deleted ? "bg-destructive/10" : ""}`}>
                    <td className="p-3 font-bold">{u.name || "No name"} {admin && <span className="chip bg-accent text-[10px] ml-1">admin</span>}</td>
                    <td className="p-3 font-semibold">{u.email}</td>
                    <td className="p-3 font-mono text-xs font-bold">{u.user_uid || u.id}</td>
                    <td className="p-3"><span className={`chip ${deleted ? "bg-destructive text-white" : "bg-mint"}`}>{deleted ? "deleted" : "active"}</span></td>
                    <td className="p-3"><span className={`chip ${u.plan === "paid" ? "bg-primary" : "bg-muted"}`}>{u.plan === "paid" && <Crown className="w-3 h-3 mr-1" strokeWidth={2.5} />}{u.plan}</span></td>
                    <td className="p-3 font-semibold">{u.rules_total}</td>
                    <td className="p-3 font-semibold">{u.triggers_total}</td>
                    <td className="p-3 font-semibold">{u.trigger_count_month || 0}</td>
                    <td className="p-3 text-xs text-muted-foreground font-semibold">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {deleted ? (
                          <button
                            disabled={Boolean(busyUser) || admin}
                            onClick={() => runUserAction(u, "recover")}
                            title="Recover account"
                            className="p-2 border-2 border-foreground rounded-md bg-mint hover:shadow-brutal-sm disabled:opacity-50"
                          >
                            <RotateCcw className="w-4 h-4" strokeWidth={2.5} />
                          </button>
                        ) : (
                          <button
                            disabled={Boolean(busyUser) || admin}
                            onClick={() => runUserAction(u, "delete")}
                            title="Delete account"
                            className="p-2 border-2 border-foreground rounded-md bg-white hover:bg-destructive hover:text-white disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                          </button>
                        )}
                        <button
                          disabled={Boolean(busyUser) || admin || !deleted}
                          onClick={() => runUserAction(u, "permanent")}
                          title="Permanently delete account"
                          className="p-2 border-2 border-foreground rounded-md bg-white hover:bg-destructive hover:text-white disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" strokeWidth={2.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "logs" && (
        <div className="brutal bg-white overflow-hidden">
          {logs.length === 0 ? (
            <div className="p-10 text-center font-bold">No webhook events yet.</div>
          ) : (
            <ul className="divide-y-2 divide-foreground">
              {logs.map((l) => (
                <li key={l.id} className="p-3 flex items-center gap-3 text-sm">
                  <Webhook className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                  <span className="chip bg-secondary text-[10px]">{l.source}</span>
                  <span className="font-bold">{l.event_type}</span>
                  <span className="text-xs text-muted-foreground font-semibold ml-auto">{new Date(l.created_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
